import { amplitude, datadogLogs, datadogRum, reactPlugin, Sentry } from '@/loadOrderSensitiveImports';
import dayjs from 'dayjs';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router';
import ResizeObserver from 'resize-observer-polyfill';
import ErrorBoundary from '@/components/ErrorBoundary';
import i18n from '@/i18n';
import reportWebVitals from '@/reportWebVitals';
import { RouterProvider } from '@/router';
import { registerVitePreloadErrorRecovery } from '@/router/vitePreloadErrorRecovery';
import { EXPECTED_ERRORS, isIgnoredErrorMessage } from '@/utils/sentry';

const isProduction = import.meta.env.VITE_APP_ENV === 'production';

amplitude.initAll(import.meta.env.VITE_AMPLITUDE_API_KEY, {
  analytics: {
    remoteConfig: { fetchRemoteConfig: true }, // pull remote SDK config from Amplitude
    autocapture: {
      attribution: true, // UTM / referrer attribution events
      pageViews: true, // SPA route changes + initial load
      sessions: true, // session start / end events
      formInteractions: true, // form starts + submits
      fileDownloads: true, // downloads of common file types
      elementInteractions: true, // click + change on instrumented elements
      frustrationInteractions: true, // rage clicks, dead clicks
      pageUrlEnrichment: true, // adds path / search to event props
      networkTracking: true, // XHR + fetch request events
      webVitals: true, // CWV (LCP, INP, CLS) on page hide
    },
    serverUrl: 'https://api2.amplitude.com/2/httpapi', // US data residency endpoint
  },
  sessionReplay: { sampleRate: isProduction ? 0.001 : 0.01 }, // 1% in dev, 0.1% in production to reduce volume
  engagement: {}, // in-product Guides & Surveys; comment out to disable
});

dayjs.extend(buddhistEra);
dayjs.locale(i18n.language);
i18n.on('languageChanged', (lng) => {
  dayjs.locale(lng);
});

// Turn this to 100 for debug
const defaultSamplePercentage = 100;
// Prod sample %. Propagated via sentry-trace/baggage to eddy-be, which inherits
// this rate for API traces (~97% of be volume) and ignores its own tracesSampleRate.
// Lowering this cuts Sentry performance units on BOTH fe and be.
const prodSamplePercentage = 0.1;

Sentry.init({
  dsn: 'https://e58659f2078775ece41f1edb8e8daa81@o1166122.ingest.us.sentry.io/4506891029512192',
  enableLogs: true,
  environment: import.meta.env.VITE_APP_ENV || 'local',
  transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
  integrations: [
    Sentry.reactRouterBrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    Sentry.globalHandlersIntegration({ onunhandledrejection: false, onerror: false }),
    Sentry.dedupeIntegration(),
    Sentry.browserTracingIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate:
    import.meta.env.VITE_APP_ENV === 'production' ? prodSamplePercentage / 100 : defaultSamplePercentage / 100, // Adjust sampling rate for traces
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [import.meta.env.VITE_APP_BASE_URL ?? 'localhost'],
  // Session Replay
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  beforeSend(event) {
    // Filter out expected error types (permission errors, device not found)
    if (event.exception?.values?.[0]) {
      const errorType = event.exception.values[0].type;
      if (EXPECTED_ERRORS.includes(errorType as (typeof EXPECTED_ERRORS)[number])) {
        return null;
      }
    }

    // Filter out expected error message patterns (client errors, network issues)
    const errorMessage = event.exception?.values?.[0]?.value || event.message;
    if (errorMessage && isIgnoredErrorMessage(errorMessage)) {
      return null;
    }

    return event;
  },
});

// clientToken is public more info here: https://docs.datadoghq.com/account_management/api-app-keys/#client-tokens
datadogRum.init({
  applicationId: '2a1111bc-f8d7-48c8-962a-23034b1ec876',
  clientToken: 'pub690a8f7224e7b041c5a04a750aba2fb7',
  site: 'ap1.datadoghq.com',
  service: 'eddy-fe',
  env: import.meta.env.VITE_APP_ENV || 'local',

  // Specify a version number to identify the deployed version of your application in Datadog
  // version: '1.0.0',
  // Tracking 1% on prod
  sessionSampleRate: import.meta.env.VITE_APP_ENV === 'production' ? prodSamplePercentage : defaultSamplePercentage,
  sessionReplaySampleRate:
    import.meta.env.VITE_APP_ENV === 'production' ? prodSamplePercentage : defaultSamplePercentage,
  defaultPrivacyLevel: 'allow',
  trackUserInteractions: true,
  plugins: [reactPlugin({ router: true })],
});

datadogLogs.init({
  clientToken: 'pubc885f7d74f29eee243db01d6f7504ba8',
  site: 'ap1.datadoghq.com',
  service: 'eddy-fe',
  env: import.meta.env.VITE_APP_ENV || 'local',
  forwardErrorsToLogs: true,
});

// src: https://github.com/necolas/react-native-web/issues/934#issuecomment-514179849
window.ResizeObserver = ResizeObserver;
registerVitePreloadErrorRecovery();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <RouterProvider />
    </ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
